/* LOAD MORE: replaces numbered pagination.
   Fetches the next page, appends its grid items with a 30ms stagger,
   updates the "X of Y" progress line. The URL is left alone — see load().

   Scrolling loads the next page on its own — the button stays as the manual
   path and the keyboard path, it is not decoration.

   AUTO-LOADING STOPS AFTER THREE PAGES, deliberately. Endless auto-append walks
   the footer away from the reader: the newsletter, the policies and the contact
   details sit below the grid, and a grid that always grows means a shopper who
   scrolls can never arrive at them. After three the button returns and the
   reader chooses. That also caps a mistaken infinite loop at three requests
   rather than the whole catalogue.

   The observer fires 600px early so the next rows are usually painted before
   the reader reaches the end. */
/* Toolbar stuck-state: shadow + hairline once the toolbar is pinned */
(() => {
  const toolbar = document.querySelector('.facets-container');
  if (!toolbar || !('IntersectionObserver' in window)) return;
  const sentinel = document.createElement('div');
  toolbar.parentElement.insertBefore(sentinel, toolbar);
  new IntersectionObserver(
    ([entry]) => toolbar.classList.toggle('kashira-toolbar-stuck', !entry.isIntersecting),
    { rootMargin: '-79px 0px 0px 0px' }
  ).observe(sentinel);
})();

if (!customElements.get('kashira-loadmore')) {
  customElements.define(
    'kashira-loadmore',
    class extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        this.progress = this.querySelector('.kashira-loadmore__progress');
        this.nextUrl = this.dataset.nextUrl;
        this.total = parseInt(this.dataset.total, 10) || 0;
        this.itemSelector = this.dataset.itemSelector || '#ProductGridContainer .product-grid > .grid__item';
        this.gridSelector = this.dataset.gridSelector || '#ProductGridContainer .product-grid';
        if (!this.button) return;

        /* Only page 1 gets the scrolling behaviour. Someone arriving directly at
           ?page=2 — an old bookmark, a crawler-followed link — should get an
           ordinary paginated page with its pagination intact, because appending
           onward from the middle of a catalogue leaves them no way back to the
           start. Leaving early also leaves .kashira-has-loadmore unset, which is
           what keeps the numbered pagination visible on those pages. */
        if (new URLSearchParams(location.search).get('page')) {
          this.hidden = true;
          return;
        }
        if (!this.nextUrl) {
          this.hidden = true;
          return;
        }
        this.button.addEventListener('click', () => {
          this.autoLoads = 0; // an explicit click re-arms the automatic run
          this.load();
        });
        document.querySelector('#ProductGridContainer')?.closest('.collection-wrapper, #MainContent')
          ?.classList.add('kashira-has-loadmore');
        this.updateProgress();

        /* Announce appended products: a shopper using a screen reader gets no
           scroll cue that the page grew underneath them. */
        this.status = document.createElement('p');
        this.status.className = 'visually-hidden';
        this.status.setAttribute('role', 'status');
        this.status.setAttribute('aria-live', 'polite');
        this.appendChild(this.status);

        this.autoLoads = 0;
        this.setupAutoLoad();
      }

      /* Load on scroll. Guarded three ways: the observer disconnects once the
         auto-load budget is spent or the pages run out, a load already in
         flight is ignored by load() itself, and a browser without
         IntersectionObserver simply keeps the button. */
      setupAutoLoad() {
        if (!('IntersectionObserver' in window)) return;
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries[0].isIntersecting) return;
            if (!this.nextUrl || this.autoLoads >= 3) return this.observer.disconnect();
            this.autoLoads += 1;
            this.load();
          },
          { rootMargin: '600px 0px' }
        );
        this.observer.observe(this);
      }

      shown() {
        return document.querySelectorAll(this.itemSelector).length;
      }

      updateProgress() {
        if (this.progress && this.total) {
          this.progress.textContent = this.progress.dataset.template
            .replace('[count]', this.shown())
            .replace('[total]', this.total);
        }
        const fill = this.querySelector('.kashira-loadmore__fill');
        if (fill && this.total) {
          fill.style.width = Math.min(100, Math.round((this.shown() / this.total) * 100)) + '%';
        }
      }

      async load() {
        if (!this.nextUrl || this.button.classList.contains('is-loading')) return;
        this.button.classList.add('is-loading');
        try {
          const res = await fetch(this.nextUrl);
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const items = doc.querySelectorAll(this.itemSelector);
          const grid = document.querySelector(this.gridSelector);
          // Under a reduced-motion preference the appended items must simply be
          // there: no fade, no transition, and no stagger delay holding content
          // back from the reader.
          const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          items.forEach((item, i) => {
            if (reduced) {
              grid.appendChild(item);
              return;
            }
            item.style.opacity = '0';
            grid.appendChild(item);
            setTimeout(() => {
              item.style.transition = 'opacity 300ms ease';
              item.style.opacity = '1';
            }, i * 30);
          });
          /* The URL is deliberately NOT rewritten to the next page. After
             appending, the screen shows products 1-20, so ?page=2 would
             describe something that is not there — and a refresh, a bookmark or
             a back from a product page would then land on four products with no
             route to the other sixteen. That is precisely what was reported.
             The paginated URLs still exist for crawlers via the pagination
             links; they are just not what the address bar claims. */
          const nextEl = doc.querySelector('kashira-loadmore');
          this.nextUrl = nextEl?.dataset.nextUrl || null;
          if (!this.nextUrl) {
            this.button.hidden = true;
            this.observer?.disconnect();
          }
          this.updateProgress();
          if (this.status) this.status.textContent = this.progress?.textContent || '';
        } catch (e) {
          window.KashiraToast?.show(this.dataset.errorText || 'Could not load more');
        } finally {
          this.button.classList.remove('is-loading');
        }
      }
    }
  );
}
